import { HttpClient, MergeDataCommon } from "_fil_local_addon/camc-cms-base/index";
import { FUND_API_SOURCES } from "_fil_local_addon/camc-cms-base/amc_fund_api.constant";

import CAMCFundFilter from "./fund_filter";
import AMCFundDataRender from "./fund_render";
import AMCFundSorter from "./fund_sorter";

class AMCFundDataList {
    constructor(fundCodeList, containerElement) {
        this.fundCodeList = fundCodeList;
        this.containerElement = containerElement;
        this.$container = $(containerElement);
        this.httpClient = new HttpClient();
        this.fundListSet = new Set();
        this.filteredData = new Set();
        this.renderer = new AMCFundDataRender(containerElement);

        // Generate unique containerId for URL parameter keys
        const rawId = this.$container.attr("id") || `fl${Date.now()}`;
        this.containerId = rawId.replace(/[^0-9]/g, "") || rawId;
        this.storageKeyPrefix = `fl${this.containerId}`;

        // Sort state
        this.currentOrderItem = null;
        this.currentIsAsc = true;
    }

    async init() {
        const fundListData = await this.getFundListData();
        this.fundListSet = this.fundListUpdate(fundListData);
        this.filteredData = this.fundListSet;

        // Hide loading indicator after data is loaded
        this.$container.find(".fund-info-searching-bar").hide();

        // Set renderer size
        this.renderer.setSize(this.fundListSet.size);

        // Restore sort state from URL
        this.currentOrderItem = this.getUrlParam("o");
        const savedIsAsc = this.getUrlParam("a");
        this.currentIsAsc = savedIsAsc !== null ? savedIsAsc === "true" : true;

        // Create sorter (always created, managed by main controller)
        this.sorter = new AMCFundSorter(this.fundListSet, this.containerElement, this.renderer, (orderItem, isAsc) =>
            this.onSortChange(orderItem, isAsc),
        );

        // Create filter if filter element exists in DOM
        const $filterEl = this.$container.find(".camc-fund-list-filter");
        if ($filterEl.length > 0) {
            this.filter = new CAMCFundFilter(this.fundListSet, this.containerElement, (filteredData) =>
                this.onFilterChange(filteredData),
            );
        }

        // Initial render with sort
        this.renderData();

        this.bindEvents();
    }

    // URL parameter helper methods
    getUrlParam(key) {
        const urlParams = new URLSearchParams(window.location.search);
        return urlParams.get(`${this.storageKeyPrefix}_${key}`);
    }

    setUrlParam(key, value) {
        const url = new URL(window.location.href);
        const paramKey = `${this.storageKeyPrefix}_${key}`;

        if (value !== null && value !== undefined && value !== "") {
            url.searchParams.set(paramKey, value);
        } else {
            url.searchParams.delete(paramKey);
        }

        window.history.replaceState({}, "", url.toString());
    }

    // Callback when sort changes
    onSortChange(orderItem, isAsc) {
        this.currentOrderItem = orderItem;
        this.currentIsAsc = isAsc;
        this.setUrlParam("o", orderItem);
        this.setUrlParam("a", isAsc);
        this.renderData();
    }

    // Callback when filter changes
    onFilterChange(filteredData) {
        this.filteredData = filteredData;
        this.sorter.updateData(this.filteredData);
        this.sorter.resetTableHead();
        this.renderData();
    }

    // Central render method
    renderData() {
        const sortedData = this.sorter.sortHandler(
            this.filteredData,
            this.currentOrderItem,
            "legalName",
            this.currentIsAsc,
        );
        this.sorter.initTableHead(this.currentOrderItem, this.currentIsAsc);
        this.renderer.renderTemplate(sortedData);
    }

    async getFundListData() {
        let options = {
            errorHandler: () => {
                showApiMessageing(null);
            },
        };
        const fundFeesData = await this.httpClient.get(FUND_API_SOURCES.FUND_LIST_SOURCE, options);
        return fundFeesData.data;
    }

    getFundType(fund) {
        const $fundType = $(fund.fundType);
        let lang = document.documentElement.lang;
        let fundType = "-";
        $fundType.each(function () {
            if (this.languageTag.split("-")[0] === lang.split("-")[0] && this.fundType) {
                fundType = this.fundType;
                return false;
            }
        });
        return fundType;
    }

    fundListUpdate(fundListData) {
        let fundListSet = new Set();

        for (let fundCode of this.fundCodeList) {
            for (let fundListDataItem of fundListData) {
                if (fundListDataItem.fundCode == fundCode) {
                    // Update the pre-rendered row using renderer
                    const $row = this.renderer.updateFundRow(
                        fundCode,
                        fundListDataItem,
                        MergeDataCommon,
                        this.getFundType.bind(this),
                    );

                    // Build fund data object for filter/sort
                    fundListSet.add({
                        fundLink: $row.find(".fund-item__info a").attr("href"),
                        fundCode: fundCode,
                        legalName: MergeDataCommon.formatString(fundListDataItem.fundName[1].shortName),
                        fundType: this.getFundType(fundListDataItem),
                        riskRating: fundListDataItem.riskRating,
                        changeInPer: MergeDataCommon.formatFundData(fundListDataItem.changeInPer).data,
                        changeInPerColor: MergeDataCommon.formatFundData(fundListDataItem.changeInPer).color,
                        nav: String(
                            MergeDataCommon.formatNumber(fundListDataItem.nav, fundListDataItem.navDecimalPlace, ""),
                        ),
                        accumulatedPrice: String(
                            MergeDataCommon.formatNumber(
                                fundListDataItem.accumulatedPrice,
                                fundListDataItem.accumulatedPriceDecimalPlace,
                                "",
                            ),
                        ),
                        priceDate: fundListDataItem.priceDate,
                        accumulatedPriceDate: fundListDataItem.accumulatedPriceDate,
                        asat: fundListDataItem.asat,
                        sixMonthPerf: MergeDataCommon.formatFundData(fundListDataItem.cumulativeReturnM6).data,
                        sixMonthPerfColor: MergeDataCommon.formatFundData(fundListDataItem.cumulativeReturnM6).color,
                        oneYearPerf: MergeDataCommon.formatFundData(fundListDataItem.cumulativeReturnY1).data,
                        oneYearPerfColor: MergeDataCommon.formatFundData(fundListDataItem.cumulativeReturnY1).color,
                        announcementLink: $row.find(".fund-links a").eq(1).attr("href"),
                        legaldocumentLink: $row.find(".fund-links a").eq(0).attr("href"),
                    });
                }
            }
        }
        return fundListSet;
    }

    bindEvents() {
        const $fundListMobile = this.$container.find(".camc-fund-list__container--mobile");

        //fund item expand and shrink，and let first item expanded by default
        $fundListMobile.on("click", ".fund-item__title", function () {
            this.classList.toggle("is-active");
            var panel = this.nextElementSibling;
            if (panel.style.maxHeight) {
                panel.style.maxHeight = null;
            } else {
                panel.style.maxHeight = panel.scrollHeight + "px";
            }
        });
    }
}

document.addEventListener("DOMContentLoaded", () => {
    // Initialize all fund data list instances on the page
    $(".camc-fund-list-wrapper").each(function () {
        const $wrapper = $(this);
        const instanceId = $wrapper.attr("id").replace("camc-fund-list-", "");
        const $codesScript = $(`#camc-fund-codes-${instanceId}`);

        if ($codesScript.length > 0) {
            try {
                const fundCodeList = JSON.parse($codesScript.text());
                if (fundCodeList && fundCodeList.length > 0) {
                    const fundDataList = new AMCFundDataList(fundCodeList, this);
                    fundDataList.init();
                }
            } catch (e) {
                console.error("Error parsing fund codes:", e);
            }
        }
    });
});

export default AMCFundDataList;
